import { useRef, useState } from 'react'
import { clamp } from '../../normalizedRect'
import { MIN_BBOX_PX } from './bboxSize'

const normalize = (
  x: number,
  y: number,
  width: number,
  height: number,
  containerWidth: number,
  containerHeight: number,
) => ({
  x: x / containerWidth,
  y: y / containerHeight,
  w: width / containerWidth,
  h: height / containerHeight,
})

export type Setters = {
  setX: (x: number) => void
  setY: (y: number) => void
  setWidth: (w: number) => void
  setHeight: (h: number) => void
}

export type RectNorm = { x: number; y: number; width: number; height: number }

/**
 * 画像上をドラッグして新しいBBoxを作成する
 * - クリック: クリック位置を中心に既定サイズのBBoxを作成
 * - ドラッグ: 始点→終点の矩形でBBoxを作成
 * - Shift+ドラッグ: 正方形に制約
 */
export const useBBoxCreation = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  _currentRect: RectNorm,
  setters: Setters,
) => {
  const [creating, setCreating] = useState(false)
  const startNormRef = useRef<{ x: number; y: number } | null>(null)
  const startPxRef = useRef<{ x: number; y: number } | null>(null)
  const pointerIdRef = useRef<number | null>(null)

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const container = containerRef.current
    if (!container) return
    const rect = container.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const normalized = normalize(x, y, 0, 0, rect.width, rect.height)
    const nx = clamp(normalized.x, 0, 1)
    const ny = clamp(normalized.y, 0, 1)
    setCreating(true)
    startNormRef.current = { x: nx, y: ny }
    startPxRef.current = { x, y }
    pointerIdRef.current = e.pointerId
    setters.setX(nx)
    setters.setY(ny)
    setters.setWidth(0)
    setters.setHeight(0)
    container.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!creating || !startNormRef.current) return
    const container = containerRef.current
    if (!container) return
    const rect = container.getBoundingClientRect()
    const px = e.clientX - rect.left
    const py = e.clientY - rect.top
    const { x: cx, y: cy } = normalize(px, py, 0, 0, rect.width, rect.height)
    let x1 = Math.min(startNormRef.current.x, cx)
    let y1 = Math.min(startNormRef.current.y, cy)
    let x2 = Math.max(startNormRef.current.x, cx)
    let y2 = Math.max(startNormRef.current.y, cy)

    if (e.shiftKey) {
      const dx = x2 - x1
      const dy = y2 - y1
      const size = Math.max(Math.abs(dx), Math.abs(dy))
      x2 = x1 + size
      y2 = y1 + size
      if (x2 > 1) {
        const overflow = x2 - 1
        x1 = Math.max(0, x1 - overflow)
        x2 = 1
      }
      if (y2 > 1) {
        const overflow = y2 - 1
        y1 = Math.max(0, y1 - overflow)
        y2 = 1
      }
    }

    const w = clamp(x2 - x1, 0, 1)
    const h = clamp(y2 - y1, 0, 1)
    const nx = clamp(x1, 0, 1 - w)
    const ny = clamp(y1, 0, 1 - h)
    setters.setX(nx)
    setters.setY(ny)
    setters.setWidth(w)
    setters.setHeight(h)
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!creating || !startPxRef.current) return
    const container = containerRef.current
    if (!container) return
    const c = container.getBoundingClientRect()
    const endPx = { x: e.clientX - c.left, y: e.clientY - c.top }
    const dist = Math.hypot(
      endPx.x - startPxRef.current.x,
      endPx.y - startPxRef.current.y,
    )
    if (dist < 3) {
      const shortSide = Math.min(c.width, c.height)
      const sizePx = shortSide * 0.1
      const wNorm = sizePx / c.width
      const hNorm = sizePx / c.height
      const { x: nx, y: ny } = normalize(
        startPxRef.current.x,
        startPxRef.current.y,
        0,
        0,
        c.width,
        c.height,
      )
      const left = clamp(nx, 0, 1 - wNorm)
      const top = clamp(ny, 0, 1 - hNorm)
      setters.setX(left)
      setters.setY(top)
      setters.setWidth(wNorm)
      setters.setHeight(hNorm)
    } else {
      const start = startNormRef.current
      if (!start) {
        setCreating(false)
        container.releasePointerCapture?.(e.pointerId)
        startNormRef.current = null
        startPxRef.current = null
        return
      }
      const { x: cx, y: cy } = normalize(
        endPx.x,
        endPx.y,
        0,
        0,
        c.width,
        c.height,
      )
      let x1 = Math.min(start.x, cx)
      let y1 = Math.min(start.y, cy)
      let x2 = Math.max(start.x, cx)
      let y2 = Math.max(start.y, cy)
      if (e.shiftKey) {
        const dx = x2 - x1
        const dy = y2 - y1
        const size = Math.max(Math.abs(dx), Math.abs(dy))
        x2 = x1 + size
        y2 = y1 + size
        if (x2 > 1) {
          const overflow = x2 - 1
          x1 = Math.max(0, x1 - overflow)
          x2 = 1
        }
        if (y2 > 1) {
          const overflow = y2 - 1
          y1 = Math.max(0, y1 - overflow)
          y2 = 1
        }
      }
      const minW = MIN_BBOX_PX / c.width
      const minH = MIN_BBOX_PX / c.height
      const w = Math.max(x2 - x1, 0)
      const h = Math.max(y2 - y1, 0)
      const w2 = Math.max(w, minW)
      const h2 = Math.max(h, minH)
      const nx = clamp(x1, 0, 1 - w2)
      const ny = clamp(y1, 0, 1 - h2)
      setters.setX(nx)
      setters.setY(ny)
      setters.setWidth(w2)
      setters.setHeight(h2)
    }
    setCreating(false)
    startNormRef.current = null
    startPxRef.current = null
    const pid = pointerIdRef.current ?? e.pointerId
    pointerIdRef.current = null
    container.releasePointerCapture?.(pid)
  }

  const cancel = () => {
    if (!creating) return
    const container = containerRef.current
    const pid = pointerIdRef.current
    setCreating(false)
    startNormRef.current = null
    startPxRef.current = null
    pointerIdRef.current = null
    if (pid != null) container?.releasePointerCapture?.(pid)
  }

  return {
    creating,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    cancel,
  }
}
